import * as Colors from '@brightlayer-ui/colors';
import { EmptyState } from '@brightlayer-ui/react-components';
import { Group } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginatedTableComponent } from '../../components/PaginatedTableComponent';
import { SearchField } from '../../components/SearchField';
import { Spinner } from '../../components/Spinner';
import { InstallerInvitationWithStatus } from '../../services/installerInvitation/types';

type InstallerInvitationsSectionProps = {
    invitations: InstallerInvitationWithStatus[];
    loading: boolean;
    error: boolean;
};

const formatDate = (isoDate: string | undefined): string => {
    if (!isoDate) return '';
    return DateTime.fromISO(isoDate).toLocal().toFormat('MM/dd/yyyy');
};

const dateSortComparator = (a: string, b: string): number => {
    // Compare ISO date strings directly (naturally sortable)
    // Returns positive if b > a (descending order)
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return b.localeCompare(a);
};

export const InstallerInvitationsSection: React.FC<InstallerInvitationsSectionProps> = ({
    invitations,
    loading,
    error,
}) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');

    const invitationRows = useMemo(
        () =>
            (Array.isArray(invitations) ? invitations : []).map((inv) => ({
                email: inv.email,
                status: inv.status,
                modifiedAt: inv.modifiedAt,
            })),
        [invitations]
    );

    const filteredInvitations = useMemo(() => {
        if (!searchQuery.trim()) return invitationRows;
        const query = searchQuery.toLowerCase();
        return invitationRows.filter(
            (inv) =>
                inv.email.toLowerCase().includes(query) ||
                inv.status.toLowerCase().includes(query) ||
                formatDate(inv.modifiedAt).toLowerCase().includes(query)
        );
    }, [invitationRows, searchQuery]);

    const columns = useMemo(
        () => [
            {
                field: 'email',
                headerName: t('MANAGE_INSTALLERS_PAGE.COLUMN_EMAIL'),
            },
            {
                field: 'status',
                headerName: t('MANAGE_INSTALLERS_PAGE.COLUMN_STATUS'),
            },
            {
                field: 'modifiedAt',
                headerName: t('MANAGE_INSTALLERS_PAGE.COLUMN_LAST_UPDATED'),
                customSort: dateSortComparator,
                valueFormatter: formatDate,
            },
        ],
        [t]
    );

    if (loading) {
        return <Spinner sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }} />;
    }

    if (error) {
        return (
            <Typography variant="body2" color="error">
                {t('ERROR.DEFAULT_ERROR_MESSAGE')}
            </Typography>
        );
    }

    return (
        <>
            {invitations.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <EmptyState
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '96px 24px',
                        }}
                        title={t('MANAGE_INSTALLERS_PAGE.EMPTY_STATE_TITLE')}
                        description={t('MANAGE_INSTALLERS_PAGE.EMPTY_STATE_DESCRIPTION')}
                        icon={<Group sx={{ fontSize: '100px', color: Colors.gray[500] }} />}
                    />
                </Box>
            ) : (
                <>
                    {/* Search field */}
                    <Box sx={{ mb: 2 }}>
                        <SearchField
                            data-testid="installer-search-field"
                            placeholder={t('MANAGE_INSTALLERS_PAGE.SEARCH_PLACEHOLDER')}
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                    </Box>

                    <PaginatedTableComponent
                        dataset={filteredInvitations}
                        columns={columns}
                        defaultRowsPerPage={10}
                        initialOrderBy="modifiedAt"
                        initialOrder="desc"
                        rowsPerPageOptions={[10, 25]}
                        labelRowsPerPage={t('MANAGE_INSTALLERS_PAGE.ROWS_PER_PAGE')}
                        emptyStateMessage={t('MANAGE_INSTALLERS_PAGE.TABLE_EMPTY')}
                        data-testid="installer-invitations-table"
                    />
                </>
            )}
        </>
    );
};
